import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RefreshCw, Search, Store, Globe, CheckCircle2, Clock, AlertTriangle, Loader2 } from 'lucide-react';
import { admin } from '@/lib/api';
import { toast } from 'sonner';

const SellerTranslationStatusTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await admin.getSellerTranslationStats();
      setData(res.data);
    } catch {
      toast.error('Ceviri istatistikleri yuklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const filtered = data?.sellers?.filter(s =>
    !search || s.seller_name.toLowerCase().includes(search.toLowerCase()) || s.seller_email.toLowerCase().includes(search.toLowerCase())
  ) || [];

  const getStatusBadge = (seller) => {
    if (seller.status === 'running') return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Devam Ediyor</Badge>;
    if (seller.missing_languages?.length === 0) return <Badge className="bg-green-100 text-green-700 border-green-200">Tamamlandi</Badge>;
    if (seller.percentage === 0) return <Badge className="bg-red-100 text-red-700 border-red-200">Baslamadi</Badge>;
    return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Kismi</Badge>;
  };

  const getStatusIcon = (seller) => {
    if (seller.status === 'running') return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
    if (seller.missing_languages?.length === 0) return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    if (seller.percentage === 0) return <AlertTriangle className="w-4 h-4 text-red-400" />;
    return <Clock className="w-4 h-4 text-amber-500" />;
  };

  const fillMissingForAll = async () => {
    try {
      setLoading(true);
      const res = await admin.translateMissingProducts({ language: 'fa' });
      toast.success(res.data.message || 'Farsça eksik çeviriler başlatıldı');
      await fetchStats();
    } catch {
      toast.error('Eksik Farsça çeviri başlatılamadı');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="seller-translation-status-tab">
      {/* Summary Cards */}
      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Toplam Bayi</p>
            <p className="text-2xl font-bold">{data.summary.total_sellers}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Toplam Urun</p>
            <p className="text-2xl font-bold">{data.summary.total_products.toLocaleString('tr-TR')}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Tam Cevrilen</p>
            <p className="text-2xl font-bold text-green-600">{data.summary.total_translated.toLocaleString('tr-TR')}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Genel Ilerleme</p>
            <p className="text-2xl font-bold text-blue-600">%{data.summary.overall_percentage}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Farsça (fa)</p>
            <p className="text-2xl font-bold text-violet-600">%{data.summary.fa_percentage ?? 0}</p>
          </Card>
        </div>
      )}

      {/* Search + Refresh */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <Input
            placeholder="Bayi ara..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
            data-testid="seller-translation-search"
          />
        </div>
        <Button size="sm" variant="outline" onClick={fetchStats} className="h-8" data-testid="seller-translation-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Yenile
        </Button>
        <Button size="sm" onClick={fillMissingForAll} disabled={loading} className="h-8 bg-violet-600 hover:bg-violet-700" data-testid="fill-missing-fa-all">
          {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Globe className="w-3.5 h-3.5 mr-1" />}
          Eksik Farsça Çevir
        </Button>
      </div>

      {/* Sellers Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bayi</TableHead>
                <TableHead className="text-center">Toplam Urun</TableHead>
                <TableHead className="text-center">Cevrilen</TableHead>
                <TableHead className="text-center">Bekleyen</TableHead>
                <TableHead className="text-center">Farsça</TableHead>
                <TableHead>Ilerleme</TableHead>
                <TableHead className="text-center">Durum</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {search ? 'Aramayla eslesen bayi bulunamadi' : 'Henuz urun bulunamadi'}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((seller) => (
                  <TableRow key={seller.seller_id} data-testid={`seller-row-${seller.seller_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Store className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div>
                          <p className="font-medium text-sm">{seller.seller_name || 'Isimsiz'}</p>
                          <p className="text-xs text-muted-foreground">{seller.seller_email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center font-mono text-sm">{seller.total_products.toLocaleString('tr-TR')}</TableCell>
                    <TableCell className="text-center font-mono text-sm text-green-600">{seller.translated_count.toLocaleString('tr-TR')}</TableCell>
                    <TableCell className="text-center font-mono text-sm text-amber-600">{seller.pending_count.toLocaleString('tr-TR')}</TableCell>
                    <TableCell className="text-center font-mono text-sm text-violet-600">%{seller.fa_percentage ?? 0}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-[140px]">
                        {getStatusIcon(seller)}
                        <div className="flex-1">
                          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                seller.missing_languages?.length === 0 ? 'bg-green-500' :
                                seller.percentage === 0 ? 'bg-gray-300' :
                                seller.status === 'running' ? 'bg-blue-500' : 'bg-amber-500'
                              }`}
                              style={{ width: `${seller.percentage}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-xs font-medium w-12 text-right">%{seller.percentage}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">{getStatusBadge(seller)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default SellerTranslationStatusTab;
